import { InverterSetupMPPTracker } from './InverterSetupMPPTracker';
import { Inverter } from './Inverter';
import { SolarPanel } from './SolarPanel';
import { MPPTracker } from './MPPTracker';

export class InverterCombination {
    mppTrackers: InverterSetupMPPTracker[];

    constructor() {
        this.mppTrackers = [];
    }

    addMPPTracker(stringCount: number, panelCount: number) {
        this.mppTrackers.push(new InverterSetupMPPTracker(stringCount, panelCount));
    }
}

export class TrackerCache {
    hashTable: { [panelCount: number]: number };
    minPanelCount: number;
    maxPanelCount: number;

    constructor() {
        this.hashTable = {};
        this.minPanelCount = 0;
        this.maxPanelCount = 0;
    }
}

export class InverterCombinator {
    tempMin: number = -10;
    tempMax: number = 70;
    acMinFactor: number = 0.8;
    acMaxFactor: number = 1.2;
    minTemp25: number;
    maxTemp25: number;

    constructor(tempMin: number, tempMax: number, acMinFactor: number, acMaxFactor: number) {
        this.tempMin = tempMin;
        this.tempMax = tempMax;
        this.minTemp25 = this.tempMin - 25;
        this.maxTemp25 = this.tempMax - 25;
        this.acMinFactor = acMinFactor;
        this.acMaxFactor = acMaxFactor;
    }

    checkStringSingleTracker(inv: Inverter, panel: SolarPanel, panelCount: number, stringCount: number, InvUmppMin: number): boolean {
        let imppMax: number = this.getImppMax(panel, stringCount);
        // TODO: improve this
        if (imppMax <= inv.mppt![0].IdcMaxSym!) {
            // Valid Impp
        } else {
            // Invalid Impp
            return false;
        }

        let umppMin: number = this.getUmppMin(panel, panelCount);
        if (umppMin >= InvUmppMin) {
            // Valid min Umpp
        } else {
            // Invalid min Umpp
            return false;
        }

        // Check max open circuit voltage against max input voltage of the inverter
        let uocMax: number = this.getUocMax(panel, panelCount);
        if (uocMax <= inv.VdcMax!) {
            // Valid Uoc
        } else {
            // Invalid Uoc
            return false;
        }
        // Check max open circuit voltage against max system voltage of the panel
        if (uocMax <= panel.Vsys!) {
            // Valid Uoc
        } else {
            // Invalid Uoc
            return false;
        }

        let umppMax: number = this.getUmppMax(panel, panelCount);
        if (umppMax <= inv.VpmMax!) {
            // Valid max Umpp
        } else {
            // Invalid max Umpp
            return false;
        }

        return true;
    }

    getUocMax(panel: SolarPanel, panelCount: number): number {
        return (panel.Voc! * panelCount) * (1 + (this.minTemp25 * panel.Vcoeff!) / 100);
    }

    getImppMax(panel: SolarPanel, stringCount: number): number {
        return (panel.Ipm! * stringCount) * (1 + (this.maxTemp25 * panel.Icoeff!) / 100);
    }

    getUmppMin(panel: SolarPanel, panelCount: number): number {
        return (panel.Vpm! * panelCount) * (1 + (this.maxTemp25 * panel.Vcoeff!) / 100);
    }

    getUmppMax(panel: SolarPanel, panelCount: number): number {
        return (panel.Vpm! * panelCount) * (1 + (this.minTemp25 * panel.Vcoeff!) / 100);
    }

    findMaxPanelCount(panel: SolarPanel, umax: number, umppMax: number): number {
        // TODO: boundary check
        let maxPanelCount = umppMax / (panel.Vpm! * (1 + (this.minTemp25 * panel.Vcoeff!) / 100)) | 0;
        let uocSingle = panel.Voc! * (1 + (this.minTemp25 * panel.Vcoeff!) / 100);
        // Umax
        let maxPanelCountUmax = umax / uocSingle | 0;
        if (maxPanelCount > maxPanelCountUmax) {
            maxPanelCount = maxPanelCountUmax;
        }
        // Usys
        let maxPanelCountUsys = panel.Vsys! / uocSingle | 0;
        if (maxPanelCount > maxPanelCountUsys) {
            maxPanelCount = maxPanelCountUsys;
        }
        return maxPanelCount;
    }

    findMinPanelCount(panel: SolarPanel, umppMin: number): number {
        return Math.ceil(umppMin / (panel.Vpm! * (1 + (this.maxTemp25 * panel.Vcoeff!) / 100)));
    }

    findMinStringCount(minPanelCount: number, maxPanelCount: number, panelTotal: number, maxStringCount: number): number {
        let result = 0;
        if (panelTotal > maxPanelCount) {
            // Find smallest number of parallel strings
            for (let i = maxPanelCount; i >= minPanelCount; i--) {
                if (panelTotal % i == 0 && (panelTotal / i | 0) <= maxStringCount) {
                    return panelTotal / i | 0;
                }
            }
        } else if (panelTotal >= minPanelCount) {
            // Single string
            result = 1;
        } else {
            result = 0;
        }
        return result;
    }

    determineEfficiency(inv: Inverter): number {
        let effFactor = 1;
        if (typeof inv.EfficiencyMax !== typeof undefined && inv.EfficiencyMax! > 0) {
            effFactor = inv.EfficiencyMax!;
        }
        return effFactor;
    }

    getMinPanelCount(panel: SolarPanel, inv: Inverter): number {
        let effFactor = this.determineEfficiency(inv);
        return Math.ceil((inv.PacMax! * this.acMinFactor) / (panel.Pdc! * effFactor));
    }

    getMaxPanelCount(panel: SolarPanel, inv: Inverter): number {
        let effFactor = this.determineEfficiency(inv);
        return Math.floor((inv.PacMax! * this.acMaxFactor) / (panel.Pdc! * effFactor));
    }

    getCombinations(panel: SolarPanel, inv: Inverter, firstOnly: boolean = false): InverterCombination[] {
        let min_panel_count = this.getMinPanelCount(panel, inv);
        let max_panel_count = this.getMaxPanelCount(panel, inv);
        let result: InverterCombination[] = [];
        for (let panelCounter: number = min_panel_count; panelCounter <= max_panel_count; panelCounter++) {
            // Single MPP tracker or combined
            let singleTrackerCombinations: InverterCombination[] = this.generateSingleTrackerCombinations(inv, panel, panelCounter);
            result = result.concat(singleTrackerCombinations);
            if (firstOnly && singleTrackerCombinations.length > 0) {
                continue;
            }
            // Multiple MPP tracker
            if (inv.mppt && inv.mppt!.length > 1) {
                let multiTrackerCombinations: InverterCombination[];
                multiTrackerCombinations = this.generateMultiTrackerCombinations(inv, panel, panelCounter);
                result = result.concat(multiTrackerCombinations);
            }
        }
        return result;
    }

    generateSingleTrackerCombinations(inv: Inverter, panel: SolarPanel, panelCounter: number): InverterCombination[] {
        let result: InverterCombination[] = [];
        let combination: InverterCombination;
        let maxPanelCount = this.findMaxPanelCount(panel, inv.VdcMax!, inv.VpmMax!);
        // TODO: improve this
        let maxStringCount = inv.mppt![0].IdcMaxSym! / panel.Ipm! | 0;
        let UmppMin = 0;
        if (inv.VpmMin) {
            UmppMin = inv.VpmMin;
        } else if (inv.UmppMinDual) {
            UmppMin = inv.UmppMinDual;
        } else if (inv.UmppMinTriple) {
            UmppMin = inv.UmppMinTriple;
        }
        if (this.checkStringSingleTracker(inv, panel, panelCounter, 1, UmppMin)) {
            // Single string
            combination = new InverterCombination();
            combination.addMPPTracker(1, panelCounter);
            result.push(combination);
        } else {
            // Find smallest number of parallel strings
            let minPanelCount = this.findMinPanelCount(panel, UmppMin);
            let minStringCount = this.findMinStringCount(minPanelCount, maxPanelCount, panelCounter, maxStringCount);
            if (minStringCount > 0) {
                let panelCountPerString = panelCounter / minStringCount | 0;
                if (this.checkStringSingleTracker(inv, panel, panelCountPerString, minStringCount, UmppMin)) {
                    combination = new InverterCombination();
                    combination.addMPPTracker(minStringCount, panelCountPerString);
                    result.push(combination);
                } else {
                    // TODO: This should not happen since [findMinStringCount] has all the checks
                    // No valid parallel strings found, skip this combination
                }
            } else {
                // No valid parallel strings found, skip this combination
            }
        }
        return result;
    }

    // TODO: this is not a correct way to find combination, need a new solution
    generateTrackerCache(inv: Inverter, panel: SolarPanel): TrackerCache[] {
        let result: TrackerCache[] = [];
        let maxPanelCount = this.findMaxPanelCount(panel, inv.VdcMax!, inv.VpmMax!);
        let UmppMin = 0;
        if (inv.VpmMin) {
            UmppMin = inv.VpmMin;
        } else if (inv.UmppMinDual) {
            UmppMin = inv.UmppMinDual;
        } else if (inv.UmppMinTriple) {
            UmppMin = inv.UmppMinTriple;
        }
        for (let i: number = 0; i < inv.mppt!.length; ++i) {
            let tracker = inv.mppt![i];
            let minPanelCount = this.findMinPanelCount(panel, UmppMin);
            let maxStringCount: number;
            if (!inv.parallel) {
                maxStringCount = 1;
            } else {
                maxStringCount = MPPTracker.getMaxStringCount(panel, tracker.IdcMaxSym);
            }
            let cache = new TrackerCache();
            cache.minPanelCount = minPanelCount;
            cache.maxPanelCount = -99999;
            for (let j: number = minPanelCount; j <= maxPanelCount; j++) {
                cache.hashTable[j] = 1;
                if (j > cache.maxPanelCount) cache.maxPanelCount = j;
                for (let k = 2; k <= maxStringCount; k++) {
                    if (typeof cache.hashTable[j * k] === typeof undefined) {
                        if (j * k > cache.maxPanelCount) cache.maxPanelCount = j * k;
                        cache.hashTable[j * k] = k;
                    }
                }
            }
            result.push(cache);
        }
        return result;
    }

    generateMultiTrackerCombinations(inv: Inverter, panel: SolarPanel, panelCounter: number): InverterCombination[] {
        let result: InverterCombination[] = [];
        let combination: InverterCombination;
        let trackerCacheList = this.generateTrackerCache(inv, panel);
        combination = new InverterCombination();
        if (this.fillTrackers(panelCounter, inv.mppt!.length, trackerCacheList, 0, combination)) {
            if (combination.mppTrackers.length > 1) {
                result.push(combination);
            }
        }
        return result;
    }

    fillTrackers(panelCounter: number, trackerCounter: number, trackerCacheList: TrackerCache[], trackerIndex: number, combination: InverterCombination): boolean {
        let commonShare = panelCounter / trackerCounter | 0;
        let remainder = panelCounter % trackerCounter;
        let first = commonShare + remainder;
        let trackerCache = trackerCacheList[trackerIndex];
        while (first <= trackerCache.maxPanelCount && first <= panelCounter) {
            if (typeof trackerCache.hashTable[first] !== typeof undefined) {
                combination.addMPPTracker(trackerCache.hashTable[first], first / trackerCache.hashTable[first] | 0);
                let rest = panelCounter - first;
                if (rest == 0) {
                    return true;
                } else if (trackerCounter > 1) {
                    let success = this.fillTrackers(rest, trackerCounter - 1, trackerCacheList, trackerIndex + 1, combination);
                    if (success) {
                        return true;
                    } else {
                        combination.mppTrackers.pop();
                        first++;
                    }
                }
            } else {
                first++;
            }
        }
        return false;
    }
}
